'use client';

import { useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';

import { CompanyPageImages, videosSectionItems } from '../markupConfig';

import SvgPlayCircle from '@/assets/icons/iconPlayCircle.svg';
import SvgMute from '@/assets/icons/mute.svg';
import SvgVolume from '@/assets/icons/volume.svg';
import { AppImageClient } from '@/shared/components/appImage';
import { LazyVideo } from '@/shared/components/lazyVideo';
import { MainContainer } from '@/shared/components/mainContainer';
import { classNames } from '@/shared/lib/classNames/classNames';

interface VideosSectionProps {
  pageTranslations: Record<string, string>;
  images: Pick<CompanyPageImages, 'line1' | 'line2'>;
  /** videosSectionItems с готовыми URL ролика и постера */
  items: (Omit<(typeof videosSectionItems)[number], 'poster'> & {
    poster: string;
  })[];
}

export const VideosSection: React.FC<VideosSectionProps> = ({
  pageTranslations,
  images,
  items,
}) => (
  <section className="relative">
    <AppImageClient
      {...images.line1}
      alt="main-line-bg"
      className="absolute top-[4%] left-0 z-0 h-auto w-full object-cover max-lg:hidden"
    />
    <AppImageClient
      {...images.line2}
      alt="main-line-bg"
      className="absolute top-[46%] -left-[15%] z-0 h-auto w-full object-cover max-lg:hidden"
    />
    <MainContainer>
      <h3 className="mx-auto mb-5 text-center text-3xl font-medium lg:mb-20 lg:w-3/5 lg:text-[44px]">
        {pageTranslations.collectDataSince1999}
      </h3>
      <div className="hidden flex-col gap-[100px] lg:flex">
        {items.map(
          (
            { src, poster, infoContent: { value, text, valueSuffix } },
            index,
          ) => (
            <VideoContainer
              key={index}
              src={src}
              text={text}
              value={value}
              valueSuffix={valueSuffix}
              poster={poster}
              reverse={index % 2 === 1}
              showMuteControl={index !== items.length - 1}
            />
          ),
        )}
      </div>
      <Swiper
        spaceBetween={12}
        slidesPerView="auto"
        className="!py-3 !pr-6 lg:!hidden"
        breakpoints={{
          1024: { enabled: false },
        }}
      >
        {items.map(({ src, poster, infoContent: { value, text } }, index) => (
          <SwiperSlide key={value}>
            <VideoContainer
              key={index}
              src={src}
              text={text}
              value={value}
              poster={poster}
              reverse={index % 2 === 1}
              showMuteControl={index !== items.length - 1}
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </MainContainer>
  </section>
);

interface VideoContainerProps {
  src: string;
  reverse?: boolean;
  poster: string;
  value: string;
  valueSuffix?: string;
  text: string;
  showMuteControl?: boolean;
}

const VideoContainer: React.FC<VideoContainerProps> = ({
  src,
  value,
  text,
  valueSuffix,
  poster,
  reverse,
  showMuteControl = true,
}) => {
  const refVideo = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);

  const handlePlayToggle = () => {
    const videoNode = refVideo.current;

    if (!videoNode) return;

    if (videoNode.paused) {
      videoNode.play();
      setIsPlaying(true);
    } else {
      videoNode.pause();
      setIsPlaying(false);
    }
  };

  const handleMuteToggle = () => {
    const videoNode = refVideo.current;
    if (!videoNode) return;
    videoNode.muted = !videoNode.muted;
    setIsMuted(videoNode.muted);
  };

  return (
    <div
      className={classNames({
        cls: 'relative flex flex-col lg:flex-row lg:gap-16',
        mods: {
          'lg:flex-row-reverse': reverse,
        },
      })}
    >
      <div className="relative lg:w-1/2">
        <button
          onClick={handlePlayToggle}
          aria-label="Play video"
          className={classNames({
            cls: 'absolute top-1/2 left-1/2 z-10 -translate-x-1/2 -translate-y-1/2 cursor-pointer transition-opacity duration-200',
            mods: {
              'pointer-events-none opacity-0': isPlaying,
              'opacity-100': !isPlaying,
            },
          })}
        >
          <SvgPlayCircle />
        </button>

        <LazyVideo
          ref={refVideo}
          src={src}
          poster={poster}
          autoPlay={false}
          loop={false}
          className="aspect-video w-full rounded-[24px] object-cover lg:max-h-[400px]"
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onClick={handlePlayToggle}
          muted={isMuted}
        />
        {showMuteControl && (
          <button
            onClick={handleMuteToggle}
            aria-label={isMuted ? 'Unmute video' : 'Mute video'}
            className="absolute right-4 bottom-4 z-10 cursor-pointer text-white outline-none"
          >
            {isMuted ? <SvgMute /> : <SvgVolume />}
          </button>
        )}
      </div>

      <div className="lg:w-1/2">
        <p
          className="text-[64px] font-light lg:mb-2 lg:text-[98px]"
          style={{
            background:
              'linear-gradient(89.77deg, #FFFFFF 5%, #192025 144.71%)',
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {value}
          <span
            style={{
              background:
                'linear-gradient(89.77deg, #FFFFFF 5%, #192025 144.71%)',
              backgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {valueSuffix ? ` ${valueSuffix}` : '+'}
          </span>
        </p>
        <p>{text}</p>
      </div>
    </div>
  );
};
